import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../api';
import { useAuth } from '../hooks/useAuth';
import StatCard from './StatCard';
import {
  DashboardMetrics,
  DashboardMetricGrid,
  DashboardPanel,
  DashboardInsightsBody,
  DashboardInsightsPanelsGrid,
  DashboardSectionTitle
} from './DashboardLayout';
import {
  AnalyticsChart,
  ChartTypeToggle,
  GroupedComparisonChart,
  StageFunnelChart
} from './DashboardCharts';
import {
  computeGlobalConversions,
  computeJobAnalytics,
  computeClientAnalytics,
  computeRecruiterAnalytics,
  computeDemographics,
  computeStageDistribution,
  computeCandidateAnalytics,
  computeRejectionAnalytics,
  computeOfferStatusAnalytics,
  computeJobStatusAnalytics,
  computeBillingAnalytics,
  filterRejectionBucket,
  filterOfferStatusBucket,
  filterJobStatusBucket,
  formatRatio,
  filterDemographicRows,
  topEntries
} from '../utils/dashboardAnalytics';
import {
  applicationColumns,
  jobAnalyticsColumns,
  clientAnalyticsColumns,
  recruiterAnalyticsColumns,
  candidateDemographicColumns,
  candidateAnalyticsColumns,
  invoiceColumns,
  mapApplications,
  mapJobAnalytics,
  mapClientAnalytics,
  mapRecruiterAnalytics,
  mapCandidateDemographics,
  mapCandidateAnalytics,
  mapInvoices,
  jobColumns,
  mapJobs
} from '../utils/dashboardColumns';
import EntityAnalyticsExplorer from './EntityAnalyticsExplorer';
import {
  buildEntityChartData,
  buildEntityPipelineCompare
} from '../utils/dashboardAnalytics';
import {
  exportDemographicsPdf,
  DEMOGRAPHIC_REPORT_CRITERIA,
  CHART_MODE_LABELS
} from '../utils/exportDemographicsPdf';

const ROLE_SCOPE_LABELS = {
  admin: 'All portal candidates in active pipeline',
  recruiter: 'Candidates on your assigned jobs',
  client: 'Candidates submitted to your jobs'
};

function ExportLink({ label = 'Export all', onClick }) {
  return (
    <button
      type="button"
      className="text-[10px] font-medium text-[hsl(var(--ds-primary))] hover:underline"
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function EntityQuickChartPanel({ title, subtitle, chartData, chartMode, onSegmentClick, isComparison, groups }) {
  return (
    <DashboardPanel title={title} subtitle={subtitle}>
      {isComparison ? (
        <GroupedComparisonChart groups={groups} onGroupClick={onSegmentClick} />
      ) : (
        <AnalyticsChart mode={chartMode} data={chartData} onSegmentClick={onSegmentClick} />
      )}
    </DashboardPanel>
  );
}

function DemographicsPdfSummary({ demographics, chartMode, roleScope }) {
  return (
    <div
      data-demographics-pdf-header
      className="mb-3 rounded-xl border border-[hsl(var(--ds-border))] bg-gradient-to-r from-[hsl(var(--ds-muted))] to-[hsl(var(--ds-surface))] px-4 py-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <h3 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">
            Candidate Demographics & Decision Insights
          </h3>
          <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mt-0.5">
            Candidates in database: <strong className="text-[hsl(var(--ds-fg))]">{demographics.candidateCount}</strong>
            {demographics.withApplicationsCount > 0 && (
              <>
                {' '}
                · With applications:{' '}
                <span className="text-[hsl(var(--ds-muted-fg))]">{demographics.withApplicationsCount}</span>
              </>
            )}
            {roleScope ? (
              <>
                {' '}
                · Scope: <span className="text-[hsl(var(--ds-muted-fg))]">{roleScope}</span>
              </>
            ) : null}
            {' '}
            · Chart view:{' '}
            <span className="text-[hsl(var(--ds-primary))] font-medium">
              {CHART_MODE_LABELS[chartMode] || chartMode}
            </span>
          </p>
        </div>
        <p className="text-[10px] text-[hsl(var(--ds-muted-fg))]">Generated {new Date().toLocaleString()}</p>
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))] mb-1.5">
        Criteria in this report
      </div>
      <div className="flex flex-wrap gap-1.5">
        {DEMOGRAPHIC_REPORT_CRITERIA.map((item, index) => (
          <span
            key={item.dataKey}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[hsl(var(--ds-primary)/0.25)] bg-[hsl(var(--ds-primary)/0.08)] text-[hsl(var(--ds-primary))] text-[10px] font-medium"
          >
            <span className="text-[hsl(var(--ds-primary)/0.65)] font-semibold">{index + 1}.</span>
            {item.title}
          </span>
        ))}
      </div>
    </div>
  );
}

function DemographicChartPanel({ title, subtitle, data, chartMode, onSegmentClick, chartKey, chartLabel }) {
  return (
    <div data-demographic-chart-panel data-chart-key={chartKey}>
      <DashboardPanel title={title} subtitle={subtitle}>
        <AnalyticsChart
          mode={chartMode}
          data={data}
          onSegmentClick={key => onSegmentClick(chartKey, key, chartLabel)}
        />
      </DashboardPanel>
    </div>
  );
}

export default function DashboardInsights({
  role,
  applications = [],
  jobs = [],
  candidates = [],
  clients = [],
  recruiters = [],
  invoices = [],
  onOpenInsight
}) {
  const { user } = useAuth();
  const [chartMode, setChartMode] = useState('combo');
  const [pdfExporting, setPdfExporting] = useState(false);
  const demographicsReportRef = useRef(null);
  const { data: branding } = useQuery({
    queryKey: ['poster-branding'],
    queryFn: () => api.get('/settings/poster-branding').then(r => r.data),
    enabled: !!user && user.role !== 'candidate'
  });

  const conversions = useMemo(() => computeGlobalConversions(applications), [applications]);
  const jobStats = useMemo(() => computeJobAnalytics(jobs, applications), [jobs, applications]);
  const clientStats = useMemo(
    () => (role === 'admin' ? computeClientAnalytics(clients, jobs, applications) : []),
    [role, clients, jobs, applications]
  );
  const recruiterStats = useMemo(() => {
    if (role === 'admin') return computeRecruiterAnalytics(recruiters, jobs, applications);
    if (role === 'recruiter' && recruiters?.length === 1) {
      return computeRecruiterAnalytics(recruiters, jobs, applications);
    }
    return [];
  }, [role, recruiters, jobs, applications]);

  const demographics = useMemo(
    () => computeDemographics(applications, candidates),
    [applications, candidates]
  );
  const stageDistribution = useMemo(() => computeStageDistribution(applications), [applications]);
  const candidateStats = useMemo(
    () => computeCandidateAnalytics(applications, candidates),
    [applications, candidates]
  );
  const rejectionStats = useMemo(() => computeRejectionAnalytics(applications), [applications]);
  const offerStatusStats = useMemo(() => computeOfferStatusAnalytics(applications), [applications]);
  const jobStatusStats = useMemo(() => computeJobStatusAnalytics(jobs), [jobs]);
  const billingStats = useMemo(
    () => (role === 'admin' || role === 'client' ? computeBillingAnalytics(invoices) : null),
    [role, invoices]
  );

  const openApps = (title, filename, apps, subtitle) =>
    onOpenInsight({
      title,
      filename,
      subtitle,
      columns: applicationColumns,
      rows: mapApplications(apps, { maskForClient: role === 'client' }),
      maskPii: role === 'client'
    });

  const openDemographicBucket = (chartKey, bucketKey, chartLabel) => {
    const filtered = filterDemographicRows(
      demographics.rows,
      chartKey,
      bucketKey,
      demographics.allCandidates
    );
    onOpenInsight({
      title: `${chartLabel}: ${bucketKey}`,
      filename: `candidates_${chartKey}_${bucketKey}`.replace(/[^a-z0-9_]+/gi, '_'),
      subtitle: `${filtered.length} candidate(s) of ${demographics.candidateCount} in database`,
      columns: candidateDemographicColumns,
      rows: mapCandidateDemographics(filtered, { maskForClient: role === 'client' }),
      maskPii: role === 'client',
      candidateProfileIds: role === 'client'
    });
  };

  const openStageBucket = (stage, count) => {
    const apps = applications.filter(a => a.stage === stage);
    openApps(`Pipeline: ${stage}`, `pipeline_${stage}`, apps, `${count} application(s) at ${stage} stage`);
  };

  const recruiterJobRatio =
    recruiterStats[0] &&
    `${recruiterStats[0].completedJobs} / ${recruiterStats[0].assignedJobs} (${recruiterStats[0].jobsCompletedPct}%)`;

  const chartModeControl = <ChartTypeToggle mode={chartMode} onChange={setChartMode} />;

  const handleDemographicsPdfExport = async () => {
    if (pdfExporting || !demographics.candidateCount) return;
    setPdfExporting(true);
    try {
      await exportDemographicsPdf({
        reportElement: demographicsReportRef.current,
        demographics,
        companyName:
          branding?.appName || user?.companyName || user?.sidebarLabel || user?.displayName || 'Paytiemp',
        reportName: 'Candidate Demographics & Decision Insights',
        roleScope: ROLE_SCOPE_LABELS[role] || role,
        chartMode
      });
    } catch (err) {
      console.error('Demographics PDF export failed:', err);
      alert('Could not generate PDF. Please try again.');
    } finally {
      setPdfExporting(false);
    }
  };

  const demographicsPdfControl = (
    <button
      type="button"
      onClick={handleDemographicsPdfExport}
      disabled={pdfExporting || !demographics.candidateCount}
      className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      title="Download landscape PDF report with all demographic charts as shown"
    >
      <svg className="w-3 h-3 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
        <path
          fillRule="evenodd"
          d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
          clipRule="evenodd"
        />
      </svg>
      {pdfExporting ? 'Generating PDF…' : 'Export PDF Report'}
    </button>
  );

  const demographicsSectionActions = (
    <div className="flex items-center gap-2 flex-wrap justify-end" data-pdf-hide>
      {demographicsPdfControl}
      {chartModeControl}
    </div>
  );

  const jobJoinedChart = useMemo(
    () => buildEntityChartData(jobStats, 'joined', 'title'),
    [jobStats]
  );
  const jobPipelineCompare = useMemo(() => buildEntityPipelineCompare(jobStats, 'title', 8), [jobStats]);
  const clientJoinedChart = useMemo(
    () => (role === 'admin' ? buildEntityChartData(clientStats, 'joined', 'name') : { chartData: {}, rowByLabel: {} }),
    [role, clientStats]
  );
  const recruiterJobsChart = useMemo(
    () =>
      role === 'admin' || role === 'recruiter'
        ? buildEntityChartData(recruiterStats, 'jobsCompleted', 'name')
        : { chartData: {}, rowByLabel: {} },
    [role, recruiterStats]
  );

  const openJobDetail = row => {
    if (!row) return;
    onOpenInsight({
      title: `Job: ${row.title}`,
      filename: `job_${row.id}`,
      columns: applicationColumns,
      rows: mapApplications(row.allApps, { maskForClient: role === 'client' }),
      subtitle: `${row.joinedCount} joined · ${row.interviewToJoinedPct}% interview→joined`,
      maskPii: role === 'client'
    });
  };

  const openClientDetail = row => {
    if (!row) return;
    onOpenInsight({
      title: `Client: ${row.name}`,
      filename: `client_${row.id}`,
      columns: applicationColumns,
      rows: mapApplications(row.clientApps, { maskForClient: role === 'client' }),
      subtitle: `${row.joinedCount} joined · ${row.jobsCompletedPct}% jobs completed`,
      maskPii: role === 'client'
    });
  };

  const openRecruiterDetail = row => {
    if (!row) return;
    onOpenInsight({
      title: `Recruiter: ${row.name}`,
      filename: `recruiter_${row.id}`,
      columns: applicationColumns,
      rows: mapApplications(row.recruiterApps, { maskForClient: role === 'client' }),
      subtitle: `${row.completedJobs}/${row.assignedJobs} jobs completed`,
      maskPii: role === 'client'
    });
  };

  return (
    <DashboardInsightsBody>
      <DashboardMetrics expanded>
        <DashboardSectionTitle>Conversion Ratios</DashboardSectionTitle>
        <DashboardMetricGrid>
          <StatCard
            label="Interview → Joined"
            value={`${conversions.interviewToJoinedPct}%`}
            color="indigo"
            hint={formatRatio(conversions.joinedCount, conversions.interviewCount)}
            onClick={() =>
              openApps(
                'Interview → Joined Pipeline',
                'interview_to_joined',
                conversions.interviewPool,
                `${conversions.joinedCount} joined of ${conversions.interviewCount} that reached interview+`
              )
            }
          />
          <StatCard
            label="Offer → Joined"
            value={`${conversions.offerToJoinedPct}%`}
            color="purple"
            hint={formatRatio(conversions.joinedCount, conversions.offerCount)}
            onClick={() =>
              openApps(
                'Offer → Joined Pipeline',
                'offer_to_joined',
                conversions.offerPool,
                `${conversions.joinedCount} joined of ${conversions.offerCount} that reached offer+`
              )
            }
          />
          <StatCard
            label="Shortlist → Offer"
            value={`${conversions.shortlistToOfferPct}%`}
            color="teal"
            hint={formatRatio(conversions.offerCount, conversions.shortlistCount)}
            onClick={() =>
              openApps(
                'Shortlisted → Offer',
                'shortlist_to_offer',
                conversions.offerPool,
                'Applications that progressed from shortlist to offer or beyond'
              )
            }
          />
          <StatCard
            label="Interview → Offer"
            value={`${conversions.interviewToOfferPct}%`}
            color="blue"
            hint={formatRatio(conversions.offerCount, conversions.interviewCount)}
            onClick={() =>
              openApps(
                'Interview → Offer',
                'interview_to_offer',
                conversions.offerPool,
                'Applications that progressed from interview to offer or beyond'
              )
            }
          />
          <StatCard
            label="Submitted → Joined"
            value={`${conversions.submitToJoinedPct}%`}
            color="emerald"
            onClick={() =>
              openApps('Successful Hires', 'joined_hires', conversions.joined, 'Joined or relieved applications')
            }
          />
          {role === 'admin' && recruiterStats.length > 0 && (
            <StatCard
              label="Avg Jobs Completed"
              value={`${Math.round(
                recruiterStats.reduce((s, r) => s + Number(r.jobsCompletedPct), 0) /
                  Math.max(recruiterStats.length, 1)
              )}%`}
              color="orange"
              onClick={() =>
                onOpenInsight({
                  title: 'Recruiter Job Completion',
                  filename: 'recruiter_job_completion',
                  columns: recruiterAnalyticsColumns,
                  rows: mapRecruiterAnalytics(recruiterStats),
                  subtitle: 'Assigned vs completed jobs per recruiter'
                })
              }
            />
          )}
          {role === 'recruiter' && recruiterStats[0] && (
            <StatCard
              label="Jobs Completed"
              value={recruiterJobRatio || '0 / 0'}
              color="orange"
              onClick={() =>
                onOpenInsight({
                  title: 'My Completed Jobs',
                  filename: 'my_completed_jobs',
                  columns: jobColumns,
                  rows: mapJobs(recruiterStats[0].completedJobList),
                  subtitle: `${recruiterStats[0].completedJobs} of ${recruiterStats[0].assignedJobs} jobs completed`
                })
              }
            />
          )}
          {role === 'client' && jobStats.length > 0 && (
            <StatCard
              label="Jobs Filled"
              value={`${jobStats.filter(j => j.completed).length} / ${jobStats.length}`}
              color="orange"
              onClick={() =>
                onOpenInsight({
                  title: 'Completed Jobs',
                  filename: 'client_completed_jobs',
                  columns: jobColumns,
                  rows: mapJobs(jobs.filter(j => jobStats.find(s => s.id === j.id)?.completed)),
                  subtitle: 'Jobs where all positions are filled or status is closed'
                })
              }
            />
          )}
        </DashboardMetricGrid>

        <DashboardSectionTitle>Pipeline Volume</DashboardSectionTitle>
        <DashboardMetricGrid>
          <StatCard
            label="Submitted"
            value={conversions.submittedCount}
            color="blue"
            onClick={() => openApps('Submitted', 'submitted', conversions.submitted)}
          />
          <StatCard
            label="Selected"
            value={conversions.selectedCount}
            color="cyan"
            onClick={() =>
              openApps(
                'Selected for Interview',
                'selected',
                applications.filter(a => a.stage === 'selected'),
                'Candidates approved to schedule interview'
              )
            }
          />
          <StatCard
            label="Not Selected"
            value={conversions.notSelectedCount}
            color="rose"
            onClick={() =>
              openApps(
                'Not Selected',
                'not_selected',
                applications.filter(a => a.stage === 'not_selected'),
                'Candidates not proceeding to interview'
              )
            }
          />
          <StatCard
            label="Interview+"
            value={conversions.interviewCount}
            color="yellow"
            onClick={() => openApps('Interview Stage Pool', 'interview_pool', conversions.interviewPool)}
          />
          <StatCard
            label="Shortlist+"
            value={conversions.shortlistCount}
            color="teal"
            onClick={() => openApps('Shortlist Stage Pool', 'shortlist_pool', conversions.shortlistPool)}
          />
          <StatCard
            label="Offer+"
            value={conversions.offerCount}
            color="purple"
            onClick={() => openApps('Offer Stage Pool', 'offer_pool', conversions.offerPool)}
          />
          <StatCard
            label="At Offer"
            value={conversions.atOfferCount}
            color="purple"
            onClick={() => openApps('Currently at Offer', 'at_offer', conversions.atOffer)}
          />
          <StatCard
            label="Joined"
            value={conversions.joinedCount}
            color="green"
            onClick={() => openApps('Joined Hires', 'joined', conversions.joined)}
          />
          <StatCard
            label="Rejected"
            value={conversions.rejectedCount}
            color="red"
            onClick={() => openApps('Rejected', 'rejected', conversions.rejected)}
          />
          <StatCard
            label="All Candidates"
            value={demographics.candidateCount}
            color="blue"
            onClick={() =>
              onOpenInsight({
                title: 'All Candidates',
                filename: 'all_candidates_demographics',
                columns: candidateDemographicColumns,
                rows: mapCandidateDemographics(demographics.rows, { maskForClient: role === 'client' }),
                subtitle: `${demographics.candidateCount} candidate(s) in database — demographics charts use this full pool`,
                maskPii: role === 'client',
                candidateProfileIds: role === 'client'
              })
            }
          />
          {demographics.withApplicationsCount > 0 && (
            <StatCard
              label="With Applications"
              value={demographics.withApplicationsCount}
              color="indigo"
              onClick={() =>
                onOpenInsight({
                  title: 'Candidates With Applications',
                  filename: 'candidates_with_applications',
                  columns: candidateDemographicColumns,
                  rows: mapCandidateDemographics(
                    demographics.rows.filter(r =>
                      (applications || []).some(a => a.candidateId === r.id)
                    ),
                    { maskForClient: role === 'client' }
                  ),
                  subtitle: 'Candidates linked to at least one application in your scope',
                  maskPii: role === 'client',
                  candidateProfileIds: role === 'client'
                })
              }
            />
          )}
          {billingStats && (
            <StatCard
              label="Invoices Paid"
              value={`${billingStats.paid} / ${billingStats.total}`}
              color="emerald"
              onClick={() =>
                onOpenInsight({
                  title: 'Paid Invoices',
                  filename: 'paid_invoices',
                  columns: invoiceColumns,
                  rows: mapInvoices(invoices.filter(i => i.status === 'paid'), clients),
                  subtitle: `${billingStats.paid} paid of ${billingStats.total} invoices`
                })
              }
            />
          )}
        </DashboardMetricGrid>
      </DashboardMetrics>

      <DashboardSectionTitle action={chartModeControl}>
        Pipeline & Outcomes
      </DashboardSectionTitle>
      <DashboardInsightsPanelsGrid>
        <DashboardPanel
          title="Pipeline by Stage"
          subtitle="Funnel view — click a stage for applications"
        >
          <StageFunnelChart stageData={stageDistribution} onStageClick={openStageBucket} />
        </DashboardPanel>

        <DashboardPanel
          title="Rejection Reasons"
          subtitle="Why candidates were rejected — click for details"
          action={
            rejectionStats.total ? (
              <ExportLink
                onClick={() =>
                  openApps(
                    'All Rejected Applications',
                    'rejections',
                    rejectionStats.rejected,
                    `${rejectionStats.total} rejected application(s)`
                  )
                }
              />
            ) : null
          }
        >
          <AnalyticsChart
            mode={chartMode}
            data={topEntries(rejectionStats.counts, 10)}
            onSegmentClick={key =>
              openApps(
                `Rejected: ${key}`,
                `rejection_${key}`,
                filterRejectionBucket(applications, key),
                `Applications rejected due to ${key}`
              )
            }
          />
        </DashboardPanel>

        <DashboardPanel
          title="Offer Status"
          subtitle="Current offer-stage breakdown"
          action={
            offerStatusStats.total ? (
              <ExportLink
                onClick={() =>
                  openApps('At Offer Stage', 'offer_status', offerStatusStats.atOffer)
                }
              />
            ) : null
          }
        >
          <AnalyticsChart
            mode={chartMode}
            data={offerStatusStats.counts}
            onSegmentClick={key =>
              openApps(
                `Offer: ${key}`,
                `offer_${key}`,
                filterOfferStatusBucket(applications, key)
              )
            }
          />
        </DashboardPanel>

        <DashboardPanel
          title="Job Status"
          subtitle="Open, in-progress, closed — click for job list"
          action={
            jobStatusStats.total ? (
              <ExportLink
                onClick={() =>
                  onOpenInsight({
                    title: 'All Jobs',
                    filename: 'job_status_all',
                    columns: jobColumns,
                    rows: mapJobs(jobs)
                  })
                }
              />
            ) : null
          }
        >
          <AnalyticsChart
            mode={chartMode}
            data={jobStatusStats.counts}
            onSegmentClick={key =>
              onOpenInsight({
                title: `Jobs: ${key}`,
                filename: `jobs_${key}`,
                columns: jobColumns,
                rows: mapJobs(filterJobStatusBucket(jobs, key))
              })
            }
          />
        </DashboardPanel>
      </DashboardInsightsPanelsGrid>

      <DashboardSectionTitle>Entity Analytics</DashboardSectionTitle>
      <EntityAnalyticsExplorer
        role={role}
        jobStats={jobStats}
        clientStats={clientStats}
        recruiterStats={recruiterStats}
        candidateStats={candidateStats}
        onOpenInsight={onOpenInsight}
      />

      <DashboardInsightsPanelsGrid columns={3}>
        <EntityQuickChartPanel
          title="Top Jobs by Joined"
          subtitle="Hires per job — click for application details"
          chartData={jobJoinedChart.chartData}
          chartMode={chartMode}
          onSegmentClick={label => openJobDetail(jobJoinedChart.rowByLabel[label])}
        />

        <EntityQuickChartPanel
          title="Interview+ vs Joined by Job"
          subtitle="Conversion funnel per job"
          isComparison
          groups={jobPipelineCompare.groups}
          onSegmentClick={group => openJobDetail(group.row)}
        />

        {role === 'admin' && clientStats.length > 0 && (
          <EntityQuickChartPanel
            title="Top Clients by Joined"
            subtitle="Hiring outcomes per client"
            chartData={clientJoinedChart.chartData}
            chartMode={chartMode}
            onSegmentClick={label => openClientDetail(clientJoinedChart.rowByLabel[label])}
          />
        )}

        {(role === 'admin' || role === 'recruiter') && recruiterStats.length > 0 && (
          <EntityQuickChartPanel
            title={role === 'admin' ? 'Recruiter Job Completion %' : 'My Job Completion %'}
            subtitle="Assigned vs completed jobs"
            chartData={recruiterJobsChart.chartData}
            chartMode="bar"
            onSegmentClick={label => openRecruiterDetail(recruiterJobsChart.rowByLabel[label])}
          />
        )}

        {billingStats && billingStats.total > 0 && (
          <DashboardPanel
            title={role === 'admin' ? 'Billing Overview' : 'My Invoices'}
            subtitle="Invoice status distribution"
            action={
              <ExportLink
                onClick={() =>
                  onOpenInsight({
                    title: role === 'admin' ? 'All Invoices' : 'My Invoices',
                    filename: 'invoices',
                    columns: invoiceColumns,
                    rows: mapInvoices(invoices, clients)
                  })
                }
              />
            }
          >
            <AnalyticsChart
              mode={chartMode}
              data={billingStats.byStatus}
              onSegmentClick={key => {
                const status = key.toLowerCase();
                onOpenInsight({
                  title: `Invoices: ${key}`,
                  filename: `invoices_${status}`,
                  columns: invoiceColumns,
                  rows: mapInvoices(invoices.filter(i => i.status === status), clients)
                });
              }}
            />
          </DashboardPanel>
        )}
      </DashboardInsightsPanelsGrid>

      <div
        ref={demographicsReportRef}
        data-demographics-pdf-root
        className="bg-[hsl(var(--ds-surface))] rounded-xl p-1"
      >
        <DashboardSectionTitle action={demographicsSectionActions}>
          Candidate Demographics & Decision Insights
        </DashboardSectionTitle>
        <DemographicsPdfSummary
          demographics={demographics}
          chartMode={chartMode}
          roleScope={ROLE_SCOPE_LABELS[role] || role}
        />
        <DashboardInsightsPanelsGrid columns={3}>
        <DemographicChartPanel
          title="Age Band"
          subtitle={`All ${demographics.candidateCount} candidates · click for details`}
          data={topEntries(demographics.ageBands)}
          chartMode={chartMode}
          onSegmentClick={openDemographicBucket}
          chartKey="age"
          chartLabel="Age band"
        />

        <DemographicChartPanel
          title="Experience Band"
          subtitle="Years of experience (all candidates)"
          data={topEntries(demographics.experienceBands)}
          chartMode={chartMode}
          onSegmentClick={openDemographicBucket}
          chartKey="experience"
          chartLabel="Experience band"
        />

        <DemographicChartPanel
          title="Fresher vs Experienced"
          subtitle="Candidate seniority split"
          data={demographics.fresherSplit}
          chartMode={chartMode}
          onSegmentClick={openDemographicBucket}
          chartKey="fresher"
          chartLabel="Seniority"
        />

        <DemographicChartPanel
          title="Education"
          subtitle="Highest qualification (all candidates)"
          data={topEntries(demographics.education, 10)}
          chartMode={chartMode}
          onSegmentClick={openDemographicBucket}
          chartKey="education"
          chartLabel="Education"
        />

        <DemographicChartPanel
          title="Area of Expertise"
          subtitle="Domain distribution for decision-making"
          data={topEntries(demographics.expertise, 10)}
          chartMode={chartMode}
          onSegmentClick={openDemographicBucket}
          chartKey="expertise"
          chartLabel="Area of expertise"
        />

        <DemographicChartPanel
          title="Core Competencies"
          subtitle="Core competencies (all candidates; tag counts may exceed headcount)"
          data={topEntries(demographics.coreCompetencies, 10)}
          chartMode={chartMode}
          onSegmentClick={openDemographicBucket}
          chartKey="coreCompetencies"
          chartLabel="Core competency"
        />

        <DemographicChartPanel
          title="Technical Skills"
          subtitle="Technical skill distribution"
          data={topEntries(demographics.technicalSkills, 10)}
          chartMode={chartMode}
          onSegmentClick={openDemographicBucket}
          chartKey="technicalSkills"
          chartLabel="Technical skill"
        />

        <DemographicChartPanel
          title="Location"
          subtitle="Geographic spread (all candidates)"
          data={topEntries(demographics.locations, 10)}
          chartMode={chartMode}
          onSegmentClick={openDemographicBucket}
          chartKey="location"
          chartLabel="Location"
        />

        <DemographicChartPanel
          title="Gender Split"
          subtitle="Gender split (all candidates)"
          data={topEntries(demographics.gender)}
          chartMode={chartMode}
          onSegmentClick={openDemographicBucket}
          chartKey="gender"
          chartLabel="Gender"
        />
        </DashboardInsightsPanelsGrid>
      </div>
    </DashboardInsightsBody>
  );
}
